import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Observable, Subject, from, of } from 'rxjs';
import { catchError, concatMap, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UmsService } from '../ums.service';

type MailStatus = 'pending' | 'generating' | 'sending' | 'sent' | 'failed' | 'skipped';

interface MailResult {
    teacher: any;
    name: string;
    email: string;
    designation: string;
    college: string;
    subject: string;
    status: MailStatus;
    message: string;
}

@Component({
    selector: 'app-send-email-dialog',
    templateUrl: './send-email-dialog.component.html',
    styleUrls: ['./send-email-dialog.component.scss']
})
export class SendEmailDialogComponent implements OnInit, OnDestroy {
    searchForm: FormGroup;
    teacherList: any[] = [];
    designationList = [
        { value: '', label: 'All' },
        { value: 'CP', label: 'Chairperson' },
        { value: 'INT', label: 'Internal' },
        { value: 'EXT', label: 'External' }
    ];
    pageSize = 10;
    pageSizeOptions: number[] = [5, 10, 20, 50];
    currentPage = 1;
    totalItems = 0;
    loading = false;
    selectingAll = false;
    selected = new Map<any, any>();

    showProgress = false;
    sending = false;
    results: MailResult[] = [];

    readonly senderEmail = 'examhelpdesk@muhs.ac.in';

    readonly statusLabels: Record<MailStatus, string> = {
        pending: 'Pending',
        generating: 'Generating letter',
        sending: 'Sending mail',
        sent: 'Sent',
        failed: 'Failed',
        skipped: 'Skipped'
    };

    readonly statusIcons: Record<MailStatus, string> = {
        pending: 'fa-clock',
        generating: 'fa-spinner fa-spin',
        sending: 'fa-spinner fa-spin',
        sent: 'fa-check-circle',
        failed: 'fa-exclamation-circle',
        skipped: 'fa-ban'
    };

    private stopRequested = false;
    private load$ = new Subject<void>();
    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private umsService: UmsService,
        public dialogRef: MatDialogRef<SendEmailDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) { }

    ngOnInit(): void {
        this.searchForm = this.fb.group({
            SearchString: new FormControl(''),
            Designation: new FormControl('')
        });

        this.load$.pipe(
            tap(() => (this.loading = true)),
            switchMap(() =>
                this.umsService.getfinalTeachers(this.buildPayload()).pipe(
                    catchError(() => of(null))
                )
            ),
            takeUntil(this.destroy$)
        ).subscribe((res: any) => {
            this.loading = false;
            if (res && res.status && res.data && res.data.length > 0) {
                this.teacherList = res.data;
                this.totalItems = res.data[0].TotalRecords;
            } else {
                this.teacherList = [];
                this.totalItems = 0;
            }
        });

        this.searchForm.get('SearchString').valueChanges.pipe(
            debounceTime(350),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.currentPage = 1;
            this.load$.next();
        });

        this.searchForm.get('Designation').valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.currentPage = 1;
            this.load$.next();
        });

        this.load$.next();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildPayload() {
        const designation = this.searchForm.get('Designation').value;
        const search = this.searchForm.get('SearchString').value;
        return {
            CoCode: this.data.CoCode,
            SubId: this.data.SubId,
            SessionId: this.data.SessionId,
            Designation: designation || '',
            SearchString: (search || '').trim(),
            PageSize: this.pageSize,
            PageNumber: this.currentPage
        };
    }

    onPageChange(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex + 1;
        this.load$.next();
    }

    isSelected(t: any): boolean {
        return this.selected.has(t.IETechId);
    }

    toggleRow(t: any, checked: boolean): void {
        checked ? this.selected.set(t.IETechId, t) : this.selected.delete(t.IETechId);
    }

    get allSelectedOnPage(): boolean {
        return this.teacherList.length > 0 && this.teacherList.every(t => this.isSelected(t));
    }

    get someSelectedOnPage(): boolean {
        return !this.allSelectedOnPage && this.teacherList.some(t => this.isSelected(t));
    }

    get allRecordsSelected(): boolean {
        return this.totalItems > 0 && this.selected.size >= this.totalItems;
    }

    toggleAllOnPage(checked: boolean): void {
        this.teacherList.forEach(t => this.toggleRow(t, checked));
    }

    selectAllRecords(): void {
        if (this.allRecordsSelected) {
            this.selected.clear();
            return;
        }
        if (this.totalItems === 0) {
            return;
        }
        this.selectingAll = true;
        const payload = { ...this.buildPayload(), PageSize: this.totalItems, PageNumber: 1 };
        this.umsService.getfinalTeachers(payload).pipe(
            catchError(() => of(null)),
            finalize(() => (this.selectingAll = false)),
            takeUntil(this.destroy$)
        ).subscribe((res: any) => {
            if (res && res.status && res.data) {
                res.data.forEach((t: any) => this.selected.set(t.IETechId, t));
            }
        });
    }

    getSelectedCount(): number {
        return this.selected.size;
    }

    get sentCount(): number {
        return this.count('sent');
    }

    get failedCount(): number {
        return this.count('failed');
    }

    get completedCount(): number {
        return this.count('sent', 'failed', 'skipped');
    }

    get remainingCount(): number {
        return this.results.length - this.completedCount;
    }

    get progressPercent(): number {
        return this.results.length > 0 ? Math.round((this.completedCount / this.results.length) * 100) : 0;
    }

    get currentItem(): MailResult | undefined {
        return this.results.find(r => r.status === 'generating' || r.status === 'sending');
    }

    sendEmail(): void {
        const teachers = Array.from(this.selected.values());
        if (teachers.length === 0 || this.sending) {
            return;
        }
        this.results = teachers.map(t => this.createResult(t));
        this.showProgress = true;
        this.runQueue(this.results);
    }

    retryFailed(): void {
        const failed = this.results.filter(r => r.status === 'failed');
        if (failed.length === 0 || this.sending) {
            return;
        }
        failed.forEach(r => {
            r.status = 'pending';
            r.message = '';
        });
        this.runQueue(failed);
    }

    stopSending(): void {
        this.stopRequested = true;
    }

    backToList(): void {
        if (this.sending) {
            return;
        }
        this.results
            .filter(r => r.status === 'sent')
            .forEach(r => this.selected.delete(r.teacher.IETechId));
        this.results = [];
        this.showProgress = false;
    }

    close(): void {
        if (this.sending) {
            return;
        }
        if (this.results.length > 0) {
            this.dialogRef.close({ sent: this.sentCount > 0, count: this.sentCount, failed: this.failedCount });
            return;
        }
        this.dialogRef.close();
    }

    private createResult(t: any): MailResult {
        return {
            teacher: t,
            name: t.TeacherName,
            email: t.Email,
            designation: t.AssignedAs,
            college: t.AssignedCollege,
            subject: t.EsName,
            status: 'pending',
            message: ''
        };
    }

    private count(...statuses: MailStatus[]): number {
        return this.results.filter(r => statuses.indexOf(r.status) !== -1).length;
    }

    private runQueue(queue: MailResult[]): void {
        this.sending = true;
        this.stopRequested = false;
        this.dialogRef.disableClose = true;

        from(queue).pipe(
            concatMap(item => this.processItem(item)),
            takeUntil(this.destroy$),
            finalize(() => {
                this.sending = false;
                this.dialogRef.disableClose = false;
            })
        ).subscribe();
    }

    private processItem(item: MailResult): Observable<void> {
        if (this.stopRequested) {
            item.status = 'skipped';
            item.message = 'Stopped by user';
            return of(undefined);
        }

        if (!item.email) {
            item.status = 'failed';
            item.message = 'No email address';
            return of(undefined);
        }

        item.status = 'generating';

        return this.generateReport(item.teacher).pipe(
            switchMap(blob => this.blobToBase64(blob).pipe(map(content => ({ blob, content })))),
            tap(() => (item.status = 'sending')),
            switchMap(({ blob, content }) => this.umsService.sendEmail(this.buildMail(item.teacher, blob, content))),
            tap((res: any) => {
                if (!res || res.success !== true) {
                    throw new Error(res && res.error ? res.error : 'Mail could not be sent');
                }
                item.status = 'sent';
                item.message = '';
            }),
            catchError(err => {
                item.status = 'failed';
                item.message = this.errorMessage(err);
                return of(null);
            }),
            map(() => undefined)
        );
    }

    private generateReport(t: any): Observable<Blob> {
        const loginId = parseInt(sessionStorage.getItem('userId'), 10) || 0;
        const extra = {
            TechId: t.TechID,
            Designation: t.Designation,
            CgCode: t.AssignedCollegeCode,
            Session: t.SessionId,
            SubId: t.SubId
        };
        const params = {
            LoginId: loginId,
            RptCode: 323,
            InstName: '',
            InstNo: this.umsService.secretdata['ClubPartTrans'] || 0,
            ShId: '',
            CgCode: t.AssignedCollegeCode,
            RollNo: '',
            Cent: '',
            TnCode: '',
            EsId: '',
            CsNo: '',
            PgBreak: '',
            StrId: '',
            StrCode: '',
            RgNo: '',
            UC: '',
            Ledger: '',
            RegnRoll: '',
            DsCode: '',
            CoCode: '',
            SnNo: '',
            Extra1: JSON.stringify(extra)
        };

        return this.umsService.SetReportParam(params).pipe(
            switchMap((res: any) => {
                if (!res || !res.status) {
                    throw new Error(res && res.message ? res.message : 'Report parameters were rejected');
                }
                return this.umsService.renderHbsReport({ LoginId: loginId, Mode: false });
            }),
            map((blob: Blob) => {
                if (!blob || blob.size === 0 || blob.type.indexOf('json') !== -1) {
                    throw new Error('Report could not be generated');
                }
                return blob;
            })
        );
    }

    private blobToBase64(blob: Blob): Observable<string> {
        return new Observable<string>(subscriber => {
            const reader = new FileReader();
            reader.onload = () => {
                subscriber.next(String(reader.result).split(',')[1]);
                subscriber.complete();
            };
            reader.onerror = () => subscriber.error(new Error('Generated report could not be read'));
            reader.readAsDataURL(blob);
        });
    }

    private buildMail(t: any, blob: Blob, content: string) {
        const fileName = `Appointment_Letter_${this.safeFileName(t.TeacherName)}.${this.fileExtension(blob.type)}`;
        return {
            from: this.senderEmail,
            to: t.Email,
            subject: `Examiner Appointment Letter - ${t.EsName}`,
            html: this.buildHtml(t),
            attachments: [
                {
                    filename: fileName,
                    content,
                    encoding: 'base64',
                    contentType: blob.type || 'application/pdf'
                }
            ],
            module: 'Examiner Appointment',
            extra: { IETechId: t.IETechId, CoCode: t.CoCode, SubId: t.SubId, SessionId: t.SessionId }
        };
    }

    private buildHtml(t: any): string {
        const row = (label: string, value: any) =>
            `<tr><td style="padding: 4px 18px 4px 0; color: #555;">${label}</td><td style="padding: 4px 0;"><b>${this.escapeHtml(value)}</b></td></tr>`;

        return `<div style="font-family: Arial, sans-serif; font-size: 14px; color: #222; line-height: 1.6;">
<p>Dear ${this.escapeHtml(t.TeacherName)},</p>
<p>You have been assigned as <b>${this.escapeHtml(t.AssignedAs)}</b>. Please find your letter attached.</p>
<table style="border-collapse: collapse; margin: 12px 0;">
${row('Designation', t.AssignedAs)}
${row('Course', t.CoName)}
${row('Subject', t.EsName)}
${row('Assigned College', t.AssignedCollege)}
${row('District', t.AssignedDistrict)}
</table>
<p>Regards,<br>Maharashtra University of Health Sciences</p>
</div>`;
    }

    private escapeHtml(value: any): string {
        return String(value === null || value === undefined ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    private safeFileName(value: any): string {
        return String(value || 'Examiner').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    }

    private fileExtension(type: string): string {
        if (type && type.indexOf('csv') !== -1) {
            return 'csv';
        }
        if (type && type.indexOf('html') !== -1) {
            return 'html';
        }
        return 'pdf';
    }

    private errorMessage(err: any): string {
        if (err && err.error) {
            if (typeof err.error === 'string') {
                return err.error;
            }
            if (err.error.error) {
                return err.error.error;
            }
            if (err.error.message) {
                return err.error.message;
            }
        }
        return err && err.message ? err.message : 'Something went wrong';
    }
}